import React, { useEffect, useState } from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Money,
} from '@reverbdotcom/commons/src/gql/graphql';
import { WarningIcon, NudgeExploreIcon, NudgeGemIcon } from '@reverbdotcom/cadence/icons/react';
import classNames from 'classnames';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { conditionUuidForSlug } from '@reverbdotcom/commons/src/condition_helpers';
import { gql } from '@reverbdotcom/commons/src/gql';
import { RCFlex, RCIcon, RCLoadingBars, RCText, RCThemedBox } from '@reverbdotcom/cadence/components';
import { debounce } from 'lodash';
import { isConditionIneligibleForEstimates } from './pricing_guidance/SellFormPricingGuidance';

const LIKELY_TO_SELL_SCORE = 0;
const LESS_LIKELY_TO_SELL_SCORE = 0.6;
const UNLIKELY_TO_SELL_SCORE = 1.3;
const MINIMUM_PRICE_CENTS = 100;

interface IExternalProps {
  calloutPrice: core_apimessages_Money;
  conditionSlug: string;
  canonicalProductId: string;
  trackEvents?: boolean;
  countryCode: string;
}

function getPriceCalloutData(
  priceCondition: string,
  estimatedNewListingPrice: core_apimessages_Money,
) {
  const calloutDataMap = {
    priceHigherThanNewListingPrice: {
      icon: <WarningIcon />,
      title: I18n.t('discovery.sellForm.pricingSection.priceSignals.higherThanNewPrice.title'),
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.higherThanNewPrice.text', {
        newPrice: estimatedNewListingPrice?.display,
      }),
      themeType: 'utility',
      variant: 'urgent-ultralight',
    },
    likelyToSell: {
      icon: <NudgeGemIcon />,
      title: I18n.t('discovery.sellForm.pricingSection.priceSignals.likelyToSell.title'),
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.likelyToSell.text'),
      themeType: 'utility',
      variant: 'info-ultralight',
    },
    aboveAverage: {
      icon: <NudgeExploreIcon />,
      iconColor: 'rc-color-accent-loud',
      title: I18n.t('discovery.sellForm.pricingSection.priceSignals.aboveAverage.title'),
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.aboveAverage.text'),
      themeType: 'surface',
      variant: 'on-primary',
    },
    lessLikelyToSell: {
      icon: <NudgeExploreIcon />,
      iconColor: 'rc-color-accent-loud',
      title: I18n.t('discovery.sellForm.pricingSection.priceSignals.lessLikelyToSell.title'),
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.lessLikelyToSell.text'),
      themeType: 'surface',
      variant: 'on-primary',
    },
    unlikelyToSell: {
      icon: <NudgeExploreIcon />,
      title: I18n.t('discovery.sellForm.pricingSection.priceSignals.unlikelyToSell.title'),
      text: I18n.t('discovery.sellForm.pricingSection.priceSignals.unlikelyToSell.text'),
      themeType: 'utility',
      variant: 'loud-ultralight',
    },
  };

  return calloutDataMap[priceCondition];
}


function findPriceCallout({ calloutPrice, estimatedNewListingPrice, priceScore, priceMiddleAmountCents }) {
  const hasPriceRecommendation = !!priceMiddleAmountCents;

  const priceHigherThanNewListingPrice = calloutPrice?.amountCents > estimatedNewListingPrice?.amountCents;
  const likelyToSell = hasPriceRecommendation && priceScore <= LIKELY_TO_SELL_SCORE;
  const aboveAverage = hasPriceRecommendation && priceScore > LIKELY_TO_SELL_SCORE && priceScore <= LESS_LIKELY_TO_SELL_SCORE;
  const lessLikelyToSell = hasPriceRecommendation && priceScore > LESS_LIKELY_TO_SELL_SCORE && priceScore <= UNLIKELY_TO_SELL_SCORE;
  const unlikelyToSell = hasPriceRecommendation && priceScore > UNLIKELY_TO_SELL_SCORE;

  const priceCallouts = [
    { condition: 'priceHigherThanNewListingPrice', active: priceHigherThanNewListingPrice },
    { condition: 'aboveAverage', active: aboveAverage },
    { condition: 'unlikelyToSell', active: unlikelyToSell },
    { condition: 'lessLikelyToSell', active: lessLikelyToSell },
    { condition: 'likelyToSell', active: likelyToSell },
  ];
  return priceCallouts.find(c => c.active)?.condition ?? null;
}

export default function SellFormPriceSignalCallout({
  calloutPrice,
  canonicalProductId,
  conditionSlug,
  countryCode,
}: IExternalProps) {
  const conditionUuid = conditionUuidForSlug(conditionSlug);
  const [debouncedCalloutPrice, setDebouncedCalloutPrice] = useState(calloutPrice);
  const debouncedSetCalloutPrice = debounce(setDebouncedCalloutPrice, 500);
  // considers the user is done typing when the debounced price is the same as the current price
  const finishedInput = calloutPrice.amountCents == debouncedCalloutPrice.amountCents;

  useEffect(() => {
    debouncedSetCalloutPrice(calloutPrice);
  }, [calloutPrice]);

  const {
    data: newPriceData,
  } = useQuery(
    ESTIMATED_NEW_LISTING_PRICE_QUERY,
    {
      skip: !canonicalProductId,
      variables: { canonicalProductId },
    },
  );

  const {
    data: priceScoresData,
    loading: priceScoresLoading,
  } = useQuery(
    PRICE_SCORE_QUERY,
    {
      skip: !canonicalProductId || isConditionIneligibleForEstimates(conditionSlug) || !finishedInput,
      variables: {
        canonicalProductId,
        conditionUuid,
        price: debouncedCalloutPrice,
        countryCode,
      },
    },
  );

  const priceHigherThanMinimumPrice = calloutPrice?.amountCents > MINIMUM_PRICE_CENTS;
  const estimatedNewListingPrice = newPriceData?.canonicalproduct?.estimatedNewListingPrice?.price;
  const priceScore = priceScoresData?.priceScores?.priceScores[0]?.priceScore;
  const priceMiddleAmountCents = priceScoresData?.priceScores?.priceScores[0]?.priceMiddle?.amountCents;

  const activePriceCallout = priceHigherThanMinimumPrice && findPriceCallout({
    calloutPrice,
    estimatedNewListingPrice,
    priceScore,
    priceMiddleAmountCents,
  });

  const calloutData = getPriceCalloutData(activePriceCallout, estimatedNewListingPrice);

  // creating a 'fake' loading between debounces to prevent flickering
  const loading = !finishedInput || priceScoresLoading;

  const visible = canonicalProductId && priceHigherThanMinimumPrice && (loading || calloutData);

  const calloutClasses = classNames(
    'sell-form-price-signal-callout', {
      'sell-form-price-signal-callout--visible': visible,
    },
  );

  return (
    <div className={calloutClasses}>
      <div>
        {loading &&
          <RCThemedBox
            themeType="surface"
            variant="on-primary"
            className="padding-5 bd-radius-md"
          >
            <RCFlex gap="2" justify="center">
              <RCLoadingBars />
            </RCFlex>
          </RCThemedBox>
        }
        {calloutData && !loading &&
          <RCThemedBox
            themeType={calloutData.themeType}
            variant={calloutData.variant}
            className="padding-4 bd-radius-md"
          >
            <RCFlex
              align="center"
              gap="2"
            >
              {calloutData.iconColor &&
                <RCText color={calloutData.iconColor}>
                  <RCIcon icon={calloutData.icon} size="rc-space-5" />
                </RCText>
              }

              {!calloutData.iconColor &&
                <RCThemedBox.Icon icon={calloutData.icon} size="rc-space-5" themed />
              }
              <RCFlex
                direction="col"
                gap="0"
              >
                <RCText.Fine weight="bold">
                  {calloutData.title}
                </RCText.Fine>
                <RCText.Fine color="rc-color-text-secondary">
                  {calloutData.text}
                </RCText.Fine>
              </RCFlex>
            </RCFlex>
          </RCThemedBox>
        }
      </div>
    </div>
  );
}

export const ESTIMATED_NEW_LISTING_PRICE_QUERY = gql(`
  query Core_SellForm_EstimatedNewListingPrice($canonicalProductId: String!) {
    canonicalproduct(input: { id: $canonicalProductId }) {
      _id
      estimatedNewListingPrice {
        price {
          amountCents
          display
        }
      }
    }
  }
`);

export const PRICE_SCORE_QUERY = gql(`
  query DataServices_SellForm_PriceScore($canonicalProductId: String!, $conditionUuid: String!, $price: Input_reverb_pricing_Money!, $countryCode: String!){
    priceScores(input: { priceScoreQueries: [{
      canonicalProductId: $canonicalProductId,
      conditionUuid: $conditionUuid,
      price: $price,
      countryCode: $countryCode
    }] }) {
      priceScores {
        priceScore
        priceMiddle {
          amountCents
        }
      }
    }
  }
 `);
